/**
 * SyncQueueManager - Gerenciador de Fila de Sincronização
 * Gerencia operações pendentes para sincronização com o servidor
 */

import { openDatabase } from './IndexedDBSetup';

const STORE_NAME = 'syncQueue';

const ENTITY_TYPES = {
  SURVEY: 'survey',
  ENVIRONMENT: 'environment',
  ITEM: 'item',
  FILE: 'file',
  SKETCH_PROJECT: 'sketch_project',
  SKETCH_ELEMENTS: 'sketch_elements',
};

const ACTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

const STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

/**
 * Gera ID único para o item da fila
 */
const generateQueueId = () => {
  const now = new Date();
  const dateStr = now.toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `SQ-${dateStr}-${random}`;
};

/**
 * Adiciona operação à fila de sincronização
 */
export const enqueue = async (entity, entityId, action, data = {}) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const queueItem = {
      queueId: generateQueueId(),
      entity,
      entityId,
      action,
      data,
      status: STATUS.PENDING,
      timestamp: new Date().toISOString(),
      attempts: 0,
      lastAttempt: null,
      error: null,
    };

    const request = store.add(queueItem);

    request.onsuccess = () => {
      resolve({ ...queueItem, id: request.result });
    };

    request.onerror = () => {
      reject(new Error(`Erro ao adicionar à fila: ${request.error}`));
    };
  });
};

/**
 * Remove item da fila
 */
export const dequeue = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Erro ao remover da fila: ${request.error}`));
  });
};

/**
 * Marca item como em progresso
 */
export const markInProgress = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Item da fila com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        status: STATUS.IN_PROGRESS,
        lastAttempt: new Date().toISOString(),
        attempts: existing.attempts + 1,
      };

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao marcar em progresso: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item da fila: ${getRequest.error}`));
  });
};

/**
 * Marca item como concluído
 */
export const markCompleted = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Item da fila com ID ${id} não encontrado`));
        return;
      }

      const updated = { ...existing, status: STATUS.COMPLETED };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao marcar concluído: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item da fila: ${getRequest.error}`));
  });
};

/**
 * Marca item como falhou
 */
export const markFailed = async (id, errorMessage) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Item da fila com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        status: STATUS.FAILED,
        error: errorMessage,
        lastAttempt: new Date().toISOString(),
      };

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao marcar falhou: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item da fila: ${getRequest.error}`));
  });
};

/**
 * Busca item da fila por ID
 */
export const getById = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar item da fila: ${request.error}`));
  });
};

/**
 * Lista todos os itens pendentes
 */
export const getPending = async () => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.getAll(STATUS.PENDING);

    request.onsuccess = () => {
      const results = request.result || [];
      results.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      resolve(results);
    };
    request.onerror = () => reject(new Error(`Erro ao buscar pendientes: ${request.error}`));
  });
};

/**
 * Lista itens por tipo de entidade
 */
export const getByEntity = async (entity) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('entity');
    const request = index.getAll(entity);

    request.onsuccess = () => {
      const results = request.result || [];
      results.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      resolve(results);
    };
    request.onerror = () => reject(new Error(`Erro ao buscar por entidade: ${request.error}`));
  });
};

/**
 * Lista todos os itens da fila
 */
export const getAll = async () => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const results = request.result || [];
      results.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      resolve(results);
    };
    request.onerror = () => reject(new Error(`Erro ao buscar fila: ${request.error}`));
  });
};

/**
 * Limpa itens concluídos
 */
export const clearCompleted = async () => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.openCursor();

    let deleted = 0;

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.status === STATUS.COMPLETED) {
          cursor.delete();
          deleted++;
        }
        cursor.continue();
      } else {
        resolve(deleted);
      }
    };

    request.onerror = () => reject(new Error(`Erro ao limpar concluídos: ${request.error}`));
  });
};

/**
 * Limpa fila inteira
 */
export const clearAll = async () => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Erro ao limpar fila: ${request.error}`));
  });
};

/**
 * Conta itens pendentes
 */
export const countPending = async () => {
  const pending = await getPending();
  return pending.length;
};

/**
 * Retry item failed (muda status de failed para pending)
 */
export const retryFailed = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Item da fila com ID ${id} não encontrado`));
        return;
      }

      if (existing.status !== STATUS.FAILED) {
        reject(new Error(`Item não está em status failed`));
        return;
      }

      const updated = {
        ...existing,
        status: STATUS.PENDING,
        error: null,
      };

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao fazer retry: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item da fila: ${getRequest.error}`));
  });
};

/**
 * Atualiza dados do item da fila
 */
export const updateData = async (id, data) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Item da fila com ID ${id} não encontrado`));
        return;
      }

      const updated = { ...existing, data: { ...existing.data, ...data } };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar dados: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item da fila: ${getRequest.error}`));
  });
};

export default {
  ENTITY_TYPES,
  ACTIONS,
  STATUS,
  enqueue,
  dequeue,
  markInProgress,
  markCompleted,
  markFailed,
  getById,
  getPending,
  getByEntity,
  getAll,
  clearCompleted,
  clearAll,
  countPending,
  retryFailed,
  updateData,
};
